import type { BaseSyntheticEvent } from 'react'
import { useForm, type UseFormReturn } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import type { AffiliatePaymentMethodRequestSchema } from '@/api/gen'
import {
  buildPaymentMethodRequest,
  isPayoutMethod,
  isWireRail,
  partnerPayoutMethodFormSchema,
  PAYOUT_DETAIL_FIELD_NAMES,
  payoutMethodFormSchema,
  type PayoutMethodFormValues,
} from '@/domain/payout-methods'
import { extractValidationFieldErrors } from '@/utils/extract-validation-detail'

export type PayoutMethodForm = UseFormReturn<PayoutMethodFormValues>

// Map a server 422's `loc`-scoped field name onto its react-hook-form path.
const serverFieldPath = (field: string): 'method' | 'rail' | `values.${string}` | null => {
  if (field === 'method' || field === 'payout_method') {
    return 'method'
  }
  if (field === 'rail') {
    return 'rail'
  }
  if (PAYOUT_DETAIL_FIELD_NAMES.has(field)) {
    return `values.${field}`
  }

  return null
}

export type UsePayoutMethodForm = {
  form: PayoutMethodForm
  /** Prefill the method (details always start blank — legacy details never map
   *  cleanly to the new structured shape, so a change is a full re-entry). The
   *  raw saved value is a plain string; non-method values fall back to unset. */
  reset: (initialMethod?: string | null) => void
  /** Submit handler for the `<form>`: validates via the resolver, and on a valid
   *  form assembles the discriminated write body (via `buildPaymentMethodRequest`,
   *  the same builder the resolver validates through) and hands it to `onValid`. */
  handleSubmit: (
    onValid: (body: AffiliatePaymentMethodRequestSchema) => void,
  ) => (event?: BaseSyntheticEvent) => void
  /** Hang a server 422's field-scoped errors on the form via `setError`.
   *  Returns `true` only when EVERY field error found a form field, so the
   *  caller skips its generic alert; if any error is unmapped the caller still
   *  runs its alert + Sentry so nothing is dropped. */
  applyServerErrors: (error: unknown) => boolean
}

type UsePayoutMethodFormOptions = {
  /** The partner's own form: refuse a submit until the partner confirms the
   *  account receives USD. The admin form leaves it off. */
  requireUsdConfirmation?: boolean
}

export const usePayoutMethodForm = ({
  requireUsdConfirmation = false,
}: UsePayoutMethodFormOptions = {}): UsePayoutMethodForm => {
  const form = useForm<PayoutMethodFormValues>({
    resolver: zodResolver(
      requireUsdConfirmation ? partnerPayoutMethodFormSchema : payoutMethodFormSchema,
    ),
    defaultValues: { method: '', rail: '', values: {}, usdOnlyConfirmed: false },
  })

  const reset = (initialMethod?: string | null) => {
    form.reset({
      method: isPayoutMethod(initialMethod) ? initialMethod : '',
      rail: '',
      values: {},
      usdOnlyConfirmed: false,
    })
  }

  const handleSubmit = (onValid: (body: AffiliatePaymentMethodRequestSchema) => void) =>
    form.handleSubmit((values) => {
      // The resolver has already validated; rebuild the write body from the same
      // builder so the discriminated shape has one owner. The guard is
      // belt-and-suspenders — a valid form always has a real method.
      if (!isPayoutMethod(values.method)) {
        return
      }
      const result = buildPaymentMethodRequest(
        values.method,
        isWireRail(values.rail) ? values.rail : null,
        values.values,
      )
      if (result.success) {
        onValid({ ...result.body, usd_only_confirmed: values.usdOnlyConfirmed })
      }
    })

  const applyServerErrors = (error: unknown): boolean => {
    const fieldErrors = extractValidationFieldErrors(error)
    if (fieldErrors === null) {
      return false
    }
    let mapped = 0
    for (const { field, message } of fieldErrors) {
      const path = serverFieldPath(field)
      if (path !== null) {
        form.setError(path, { type: 'server', message })
        mapped += 1
      }
    }

    // Suppress the caller's generic alert only when every error found a field.
    // If any didn't map, return false so the alert + Sentry still surface it —
    // a signal must never vanish just because a sibling error was mapped.
    return mapped > 0 && mapped === fieldErrors.length
  }

  return { form, reset, handleSubmit, applyServerErrors }
}
